import React, { useEffect } from 'react';
import { Invoice } from '../data';

type TimelineEvent = {
  id: string;
  at: string;
  actor: 'you' | 'customer' | 'vela' | 'system';
  title: string;
  detail?: string;
};

function buildTimeline(invoice: Invoice): TimelineEvent[] {
  const events: TimelineEvent[] = [
    { id: 'e1', at: invoice.issuedOn, actor: 'you', title: `Invoice ${invoice.number} created` },
    { id: 'e2', at: invoice.issuedOn, actor: 'you', title: `Sent to ${invoice.customer}`, detail: 'via email' },
  ];
  if (invoice.status === 'overdue') {
    events.push({ id: 'e3', at: invoice.dueOn, actor: 'system', title: 'Due date passed', detail: `${invoice.daysOverdue} days overdue` });
    events.push({ id: 'e4', at: invoice.dueOn, actor: 'vela', title: 'Vela drafted a chase email', detail: 'Awaiting your review' });
  }
  if (invoice.status === 'paid') {
    events.push({ id: 'e5', at: invoice.dueOn, actor: 'customer', title: 'Payment received', detail: `$${invoice.amount.toLocaleString()}` });
  }
  return events.reverse();
}

const ACTOR_LABEL: Record<TimelineEvent['actor'], string> = {
  you: 'You', customer: 'Customer', vela: 'Vela', system: 'System',
};
const ACTOR_COLOR: Record<TimelineEvent['actor'], string> = {
  you: '#0F6E56', customer: '#1E40AF', vela: '#6B46C1', system: '#64748B',
};

const OVERLAY: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(14,26,26,0.3)', zIndex: 40,
};
const DRAWER: React.CSSProperties = {
  position: 'fixed', top: 0, right: 0, bottom: 0,
  width: 420, maxWidth: '90vw',
  background: '#FFFFFF',
  boxShadow: '-20px 0 40px -10px rgba(14,26,26,0.2)',
  zIndex: 41, display: 'flex', flexDirection: 'column',
};
const HEAD: React.CSSProperties = {
  padding: '18px 22px', borderBottom: '1px solid #E7EAF0',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
};
const HEAD_H: React.CSSProperties = { fontSize: 18, fontWeight: 700, margin: '0 0 2px' };
const HEAD_SUB: React.CSSProperties = { fontSize: 12.5, color: '#66748B' };
const CLOSE: React.CSSProperties = {
  width: 30, height: 30, borderRadius: 8,
  background: 'transparent', border: 0, color: '#66748B',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', flexShrink: 0,
};
const BODY: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '18px 22px' };
const SUMMARY: React.CSSProperties = {
  background: '#F7F8FA', borderRadius: 10,
  padding: '12px 14px', marginBottom: 20,
  fontSize: 13, color: '#0E1A1A',
};
const SECTION_LBL: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: '#66748B',
  letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 6,
};
const TIMELINE: React.CSSProperties = { position: 'relative', paddingLeft: 20, marginTop: 10 };
const LINE: React.CSSProperties = {
  position: 'absolute', left: 7, top: 6, bottom: 6,
  width: 2, background: '#E7EAF0',
};
const EVT: React.CSSProperties = { position: 'relative', paddingBottom: 18 };
const DOT: React.CSSProperties = {
  position: 'absolute', left: -17, top: 4,
  width: 10, height: 10, borderRadius: '50%',
  border: '2px solid #FFFFFF',
  boxShadow: '0 0 0 1px #E7EAF0',
};
const EVT_TITLE: React.CSSProperties = { fontSize: 13.5, fontWeight: 600, color: '#0E1A1A' };
const EVT_META: React.CSSProperties = { fontSize: 11.5, color: '#66748B', marginTop: 2 };
const EVT_DETAIL: React.CSSProperties = { fontSize: 12.5, color: '#66748B', marginTop: 2 };

type Props = {
  invoice: Invoice;
  onClose: () => void;
};

export default function ActivityDrawer({ invoice, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const events = buildTimeline(invoice);

  return (
    <>
      <div style={OVERLAY} onClick={onClose} />
      <aside style={DRAWER} role="dialog" aria-label="Invoice activity">
        <div style={HEAD}>
          <div>
            <h3 style={HEAD_H}>{invoice.number}</h3>
            <div style={HEAD_SUB}>{invoice.customer}</div>
          </div>
          <button type="button" style={CLOSE} onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div style={BODY}>
          <div style={SUMMARY}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#66748B' }}>Amount</span>
              <b>${invoice.amount.toLocaleString()}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#66748B' }}>Issued</span>
              <span>{invoice.issuedOn}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#66748B' }}>Due</span>
              <span>{invoice.dueOn}</span>
            </div>
          </div>

          <div style={SECTION_LBL}>Activity</div>

          <div style={TIMELINE}>
            <div style={LINE} />
            {events.map((e) => (
              <div key={e.id} style={EVT}>
                <div style={{ ...DOT, background: ACTOR_COLOR[e.actor] }} />
                <div style={EVT_TITLE}>{e.title}</div>
                <div style={EVT_META}>
                  <b style={{ color: ACTOR_COLOR[e.actor] }}>{ACTOR_LABEL[e.actor]}</b> {'\u00B7'} {e.at}
                </div>
                {e.detail && <div style={EVT_DETAIL}>{e.detail}</div>}
              </div>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
}